/**
 * Governance landing — question-first hub. KPI summary → recommended
 * reports → full catalog. Only wired reports (User Access Review, RBAC
 * Change History) navigate; the rest are marked "Coming soon".
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Users as UsersIcon, ShieldCheck, GitBranch, Lock, FileCheck, ArrowRight,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import api from "@/lib/api";
import { toast } from "sonner";
import { ScopeBadge, KpiDeck, ExportButtons, ReportHeader } from "@/components/governance/kit";
import GlossaryHelp from "@/components/common/GlossaryHelp";

const RECOMMENDED = [
    { id: "uar", to: "/governance/user-access-review", Icon: UsersIcon, title: "User Access Review", desc: "Who has access, what role, active or dormant, and what needs review.", meta: "Flagship", flag: true, live: true },
    { id: "priv", to: "/governance/privileged-access", Icon: ShieldCheck, title: "Privileged Access Review", desc: "All elevated accounts and whether their privileges are still justified.", meta: "Live", live: true },
    { id: "rbac", to: "/governance/rbac-change-history", Icon: GitBranch, title: "RBAC Change History", desc: "Every permission and role change — actor, before → after, source.", meta: "Live", live: true },
    { id: "auth", to: "/governance/r/login_events", Icon: Lock, title: "Authentication & Login", desc: "Sign-ins, password changes and reset requests (failed logins not captured).", meta: "Live", live: true },
    { id: "deprov", to: "/governance/r/deprovisioning", Icon: FileCheck, title: "Deprovisioning / Leavers", desc: "Account offboarding — deactivations, deletions and revoked access.", meta: "Live", live: true },
];

const CATALOG = [
    { dom: "Access & Identity", items: [["User Access Review", "/governance/user-access-review"], ["Privileged Access Review", "/governance/privileged-access"], ["RBAC Change History", "/governance/rbac-change-history"], ["Deprovisioning / Leavers", "/governance/r/deprovisioning"], ["Authentication & Login Events", "/governance/r/login_events"]] },
    { dom: "Security & Compliance", items: [["Consent & Terms of Service"], ["Security Activity / Audit Logs", "/governance/r/security_activity"], ["Login & Auth Events", "/governance/r/login_events"], ["Policy / Config Changes", "/governance/r/policy_config"]] },
    { dom: "Data & Platform", items: [["Data Access", "/governance/r/data_access"], ["Connector / Integration Activity", "/governance/r/connector_activity"], ["Dataset / Data Lifecycle", "/governance/r/dataset_lifecycle"], ["Configuration Changes", "/governance/r/config_changes"]] },
    { dom: "Usage & Operations", items: [["Usage Summary", "/governance/usage-summary"], ["Scheduled Jobs", "/governance/scheduled-jobs"], ["Workspace Activity", "/governance/r/workspace_activity"], ["API / Integration Usage", "/governance/r/api_usage"]] },
    { dom: "Billing & Commercial", items: [["Billing Activity", "/governance/r/billing_activity"], ["Subscription Changes", "/governance/r/subscription_changes"], ["Usage / Cost Summary", "/governance/cost-summary"], ["Connector Add-on Spend", "/governance/connector-addon-spend", true]] },
];

export default function Governance() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);

    useEffect(() => {
        api.get("/governance/summary").then((r) => setData(r.data)).catch(() => setData(null));
    }, []);

    const k = data?.kpis || {};
    const kpis = [
        { label: "Total users", value: k.total_users ?? "—" },
        { label: "Privileged users", value: k.privileged_users ?? "—", sub: "admins" },
        { label: "Dormant > 90d", value: k.dormant_users ?? "—", tone: (k.dormant_users ? "warn" : undefined) },
        { label: "Locked accounts", value: k.locked_accounts ?? "—", tone: (k.locked_accounts ? "alert" : undefined) },
        { label: "RBAC changes 30d", value: k.rbac_changes_30d ?? "—" },
        { label: "Access reviews", notCaptured: true, note: k.pending_access_reviews?.note },
    ];

    const openSoon = (title) => toast.info(title, { description: "This governance report is coming in a later phase." });

    return (
        <div className="p-6 max-w-[1400px] mx-auto" data-testid="governance-landing">
            <ReportHeader
                eyebrow="Compliance & Oversight"
                title="Governance"
                accent="overview"
                description="Answer the questions auditors and admins actually ask — who has access, what changed, who signed in, what needs review — then drill into any report. Scoped to your role automatically."
                actions={<><GlossaryHelp area="governance" label="Definitions" testId="gov-definitions" /><ScopeBadge user={user} scope={data?.scope} /><ExportButtons csvPath="/governance/access-review/export.csv" csvName="analyzio-user-access-review.csv" /></>}
            />

            <KpiDeck items={kpis} />

            <div className="flex items-baseline justify-between mb-3">
                <h2 className="text-base font-semibold">Recommended reports</h2>
                <span className="text-xs text-muted-foreground">Most-used for access recertification & audit</span>
            </div>
            <div className="grid grid-cols-5 gap-3 mb-8 max-[1200px]:grid-cols-2">
                {RECOMMENDED.map((r) => (
                    <button key={r.id}
                        onClick={() => r.live ? navigate(r.to) : openSoon(r.title)}
                        className={`text-left bg-card border rounded-sm p-4 flex flex-col gap-2 min-h-[150px] transition-transform hover:-translate-y-0.5 ${r.flag ? "border-foreground/50" : "border-border hover:border-foreground/35"} ${!r.live ? "opacity-70" : ""}`}
                        data-testid={`gov-rec-${r.id}`}>
                        <div className="flex items-start justify-between">
                            <span className="w-8 h-8 rounded-sm bg-muted flex items-center justify-center"><r.Icon className="h-4 w-4" strokeWidth={1.75} /></span>
                            {r.flag
                                ? <span className="text-[9px] uppercase tracking-wider bg-foreground text-background rounded-sm px-1.5 py-0.5 font-semibold">Flagship</span>
                                : <span className="text-[9px] uppercase tracking-wider text-muted-foreground border border-border rounded-sm px-1.5 py-0.5">{r.meta}</span>}
                        </div>
                        <div className="text-sm font-semibold">{r.title}</div>
                        <div className="text-[11.5px] text-muted-foreground flex-1">{r.desc}</div>
                    </button>
                ))}
            </div>

            <div className="flex items-baseline justify-between mb-3">
                <h2 className="text-base font-semibold">All reports</h2>
                <span className="text-xs text-muted-foreground">5 domains · read-only in v1</span>
            </div>
            <div className="bg-card border border-border rounded-sm grid grid-cols-5 max-[1100px]:grid-cols-2" data-testid="gov-catalog">
                {CATALOG.map((c, ci) => (
                    <div key={c.dom} className={`p-4 border-b border-border ${ci < 4 ? "border-r" : ""} max-[1100px]:border-r-0`}>
                        <div className="eyebrow !text-[10px] mb-3">{c.dom}</div>
                        <ul className="flex flex-col gap-0.5">
                            {c.items.filter(([, , saOnly]) => !saOnly || user?.role === "super_admin").map(([label, to]) => (
                                <li key={label}>
                                    <button
                                        data-testid={`gov-cat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                                        onClick={() => to ? navigate(to) : openSoon(label)}
                                        className={`w-full text-left text-[12.5px] px-2 py-1.5 rounded-sm flex items-center gap-2 hover:bg-muted ${to ? "" : "text-muted-foreground"}`}>
                                        <span className="w-1 h-1 rounded-full bg-foreground/50 shrink-0" />
                                        <span className="flex-1">{label}</span>
                                        {to ? <ArrowRight className="h-3 w-3 opacity-40" /> : <span className="text-[8.5px] uppercase tracking-wider border border-border rounded-sm px-1">soon</span>}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>

            <div className="bg-[hsl(var(--warning))]/[0.06] border border-[hsl(var(--warning))]/35 rounded-sm p-3.5 text-[12.5px] text-[hsl(38_60%_26%)] dark:text-[hsl(var(--warning))] mt-6" data-testid="gov-compliance-note">
                Governance reports reflect only the data that Analyzio currently records. They are intended to provide clear visibility into system activity, access, and changes. Where a data point is not available or not captured, the report will clearly display "Not tracked / Not captured" rather than showing an assumed or fabricated value.
            </div>
        </div>
    );
}
